import { ComponentType } from '@angular/cdk/portal';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorInputFieldComponent, FileException } from '@cargos/sprintpay-models';
import { FileRequestService } from '@cargos/sprintpay-services';
import { FieldFiles, FileData, FileEvent, FileForm, FileHandlerService } from '@cargos/sprintpay-ui';
import { Observable, Subject, forkJoin, map, switchMap, take, takeUntil } from 'rxjs';
import {
    ModuleMFAEnum,
    ResponseMFA,
    TypeModuleMFA,
} from 'src/app/modules/two-factor/verification-code/models/two-factor-models';
import { TwoFactorComponent } from 'src/app/modules/two-factor/verification-code/two-factor.component';
import { UserSessionService } from 'src/app/services';
import { FacilityAPIService } from 'src/app/services/requests/facility-api.service';
import { ReuploadValidationResponse } from 'src/app/services/requests/models/reupload-validation-response-model';
import { ErrorHandlerService } from 'src/app/services/utils/error-handler.service';
import { regex } from 'src/app/utils/constants';
import Swal from 'sweetalert2';
import { EmailFormGroupType } from '../models/form-types';

@Component({
    selector: 'app-common-signup-upload',
    templateUrl: './common-signup-upload.component.html',
    styleUrl: './common-signup-upload.component.scss',
    standalone: false,
})
export class CommonSignupUploadComponent implements OnInit, OnDestroy {
    @ViewChild('twoFactorModal') twoFactorModal?: ComponentType<TwoFactorComponent>;
    @Input() type: string;
    public readonly emailForm: EmailFormGroupType;
    public errorList: ErrorInputFieldComponent[];
    public state: string = 'email';
    public readonly module: TypeModuleMFA = ModuleMFAEnum.FACILITY_APPLICATION_REUPLOAD;
    public id: string = '';
    public fileType: string = '';
    public uuid: string = '';
    public readonly filesWhiteList: string[] = ['application/pdf'];
    public uploading: boolean = false;
    public readonly titleW9: string = 'Upload W9';
    public readonly titleBankLetter: string = 'Upload Bank Letter';
    public readonly subtitle: string = 'Accepted file type: PDF';
    public fileToUpload: string = '';
    private readonly unsubscribe$ = new Subject<void>();
    public filesUploaded: FieldFiles[] = [];

    constructor(
        private readonly fb: NonNullableFormBuilder,
        private readonly route: ActivatedRoute,
        private readonly facilityApiService: FacilityAPIService,
        private readonly userSessionService: UserSessionService,
        private readonly router: Router,
        private readonly matDialog: MatDialog,
        private readonly fileHandlerService: FileHandlerService,
        private readonly fileRequestService: FileRequestService,
        private readonly errorHandlerService: ErrorHandlerService
    ) {
        this.emailForm = this.fb.group({
            email: [{ value: '', disabled: true }, [Validators.pattern(regex.email)]],
        });
    }

    ngOnInit(): void {
        this.subscribeFilesUploaded();

        this.userSessionService
            .signInAsGuest()
            .pipe(
                switchMap(() => this.route.queryParams),
                switchMap((params) => {
                    this.id = params['id'];
                    this.fileType = params['fileType'];
                    this.uuid = params['UUID'];
                    return this.facilityApiService.getUpload(this.id, this.fileType, this.uuid);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (response: ReuploadValidationResponse): void => {
                    this.emailInput?.setValue(response?.email);
                },
                error: (error): void => {
                    const errorMsg = this.errorHandlerService.getHttpErrorsResponse(error);
                    Swal.fire({
                        html: `${errorMsg}` || 'We are having problems, Please try again.',
                        icon: 'error',
                        showConfirmButton: false,
                        showCancelButton: true,
                        cancelButtonText: 'Close',
                        allowOutsideClick: false,
                    });
                },
            });

        this.errorList = [
            {
                code: 'required',
                message: `Required field`,
            },
            {
                code: 'pattern',
                message: `Oops. The email address was malformed! Please try again`,
            },
        ];
    }

    get emailInput(): AbstractControl<string> | null {
        return this.emailForm.get('email') as AbstractControl<string> | null;
    }

    validateEmail(): void {
        this.state = 'upload';
    }

    uploadDocument(): void {
        this.matDialog.open(this.twoFactorModal as ComponentType<TwoFactorComponent>, {
            id: 'two-factor',
            disableClose: false,
            data: {
                mfaType: 'email',
                contact: this.emailForm.controls['email'].value,
                dynamicParameter: {
                    applicationId: this.id,
                    facilityOnboardingAttachmentType: this.fileType,
                    fileUUID: this.uuid,
                    [this.fileType === 'W9_FILE' ? 'w9File' : 'voidFile']: this.fileToUpload,
                },
                accountVerifiedHandler: (response: ResponseMFA) => {
                    if (response.isVerified) {
                        this.state = 'completed';
                    }
                },
            },
        });
    }

    filesAdded(uploadedFiles: FileEvent): void {
        if (this.uploading) {
            return;
        }

        this.uploading = true;

        this.uploadFiles(uploadedFiles.files)
            .pipe(take(1))
            .subscribe({
                next: (locationPaths: { names: string; size: number }[]) => {
                    this.uploading = false;
                    const existingFiles =
                        this.filesUploaded.find((field) => field.files && field.fieldName === uploadedFiles.fieldName)
                            ?.files ?? [];
                    const newLocationPaths: FileData[] = locationPaths;
                    const newFileNames: FileData[] = [...newLocationPaths, ...existingFiles];
                    this.fileHandlerService.setPathFiles({ fieldName: uploadedFiles.fieldName, files: newFileNames });
                    const file = uploadedFiles.files[0].formData.get('file') as File;

                    if (file) {
                        const reader = new FileReader();
                        reader.onload = (e: any) => {
                            const base64String = e.target.result.split(',')[1];
                            const fileDetails = {
                                bodyBase64: base64String,
                                name: file.name,
                                type: file.type,
                            };

                            this.fileToUpload = JSON.stringify(fileDetails);
                            this.uploading = false;
                        };
                        reader.readAsDataURL(file);
                    }
                },
                error: (error: FileException) => {
                    this.uploading = false;

                    Swal.fire({
                        title: error.title,
                        text: error.description,
                        icon: 'warning',
                    });
                },
            });
    }

    subscribeFilesUploaded(): void {
        this.fileHandlerService
            .getAllPathFiles()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (filesUploaded) => {
                    this.filesUploaded = filesUploaded;

                    if (filesUploaded.find((file) => file.fieldName === 'fileUpload')?.files?.length === 0) {
                        this.fileToUpload = '';
                    }
                },
            });
    }

    uploadFiles(files: FileForm[]): Observable<{ names: string; size: number }[]> {
        return forkJoin(
            files.map((file) =>
                this.fileRequestService.uploadFile(file.formData).pipe(
                    take(1),
                    map((locationPath) => ({
                        names: locationPath,
                        size: file.size,
                    }))
                )
            )
        );
    }

    getFileSizeInMB(fileSize: number): string {
        const sizeInMB = fileSize / (1024 * 1024);
        return `${sizeInMB.toFixed(2)} M`;
    }

    navigateToCargosprint(): void {
        this.router.navigateByUrl('https://cargosprint.com');
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
